import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../authentication_components/authContext';
import { Lock, Mail, Eye, EyeOff, Shield, User, AlertCircle } from 'lucide-react';

export default function AdminLogin() {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [loginType, setLoginType] = useState('admin'); // 'admin' or 'master'
  const [showForgotPasswordScreen, setShowForgotPasswordScreen] = useState(false);

  const { login, loading, isAuthenticated } = useAuth();

  // Auto-detect login type based on email input
  useEffect(() => {
    if (formData.email === 'JAY_JJPRODUCTIONS_JAYDEVSPR_PRLOCAL') {
      setLoginType('master');
    } else {
      setLoginType('admin');
    }
  }, [formData.email]);

  // Redirect authenticated users to portal - MOVED AFTER ALL HOOKS
  if (isAuthenticated) {
    return <Navigate to="/portal" replace />;
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear validation and submit errors when user starts typing
    if (errors[name] || errors.submit) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
        submit: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email) {
      newErrors.email = 'El correo electrónico es requerido';
    } else if (loginType === 'admin' && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Por favor ingrese un correo electrónico válido';
    }
    if (!formData.password) {
      newErrors.password = 'La contraseña es requerida';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    try {
      const result = await login(formData.email, formData.password);
      if (!result.success) {
        // The `login` function now provides a detailed message and optional status
        setErrors({ submit: result.message, status: result.status });
      } else {
        console.log(`${result.loginType} login successful`);
        // Navigation to /portal will happen automatically via the redirect above
      }
    } catch (error) {
      setErrors({ submit: 'Error de conexión. Por favor intente nuevamente.' });
    }
  };

  const handleForgotPassword = () => setShowForgotPasswordScreen(true);
  const handleBackToLogin = () => setShowForgotPasswordScreen(false);

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-orange-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-2xl p-8 border border-orange-200">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold bg-gradient-to-r from-black to-gray-700 bg-clip-text text-transparent mb-2">
              JaydevsPR
            </h1>
            <div className="flex items-center justify-center mb-2">
              {loginType === 'master' ? (
                <Shield className="h-6 w-6 text-red-500 mr-2" />
              ) : (
                <User className="h-6 w-6 text-orange-500 mr-2" />
              )}
              <h2 className={`text-xl font-semibold ${loginType === 'master' ? 'text-red-800' : 'text-gray-800'}`}>
                {loginType === 'master' ? 'Portal de Administración Master' : 'Portal de Administración'}
              </h2>
            </div>
            <p className={`${loginType === 'master' ? 'text-red-600' : 'text-gray-600'}`}>
              {loginType === 'master' ? 'Acceso de nivel maestro detectado' : 'Inicia sesión para acceder a tu panel de control'}
            </p>
          </div>

          {/* Forgot Password Screen */}
          {showForgotPasswordScreen ? (
            <div className="text-center space-y-6">
              <div className="mb-8">
                <AlertCircle className="h-16 w-16 text-orange-500 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Recuperación de Contraseña</h2>
                <p className="text-gray-600">Asistencia para restablecer tus credenciales de acceso</p>
              </div>
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-6 text-left">
                <h3 className="font-semibold text-orange-800 mb-4 text-lg">¿Olvidaste tu contraseña?</h3>
                <div className="space-y-4 text-gray-700">
                  <p>Para restablecer tu contraseña de administrador, necesitas contactar a tu supervisor directo o al departamento de IT.</p>
                  <div className="bg-white rounded-md p-4 border border-orange-300">
                    <h4 className="font-medium text-gray-800 mb-2">Pasos a seguir:</h4>
                    <ol className="list-decimal list-inside space-y-2 text-sm">
                      <li>Contacta a tu supervisor inmediato</li>
                      <li>Proporciona tu correo electrónico registrado</li>
                      <li>Solicita el restablecimiento de contraseña</li>
                      <li>Espera la confirmación del cambio</li>
                    </ol>
                  </div>
                  <p className="text-sm text-gray-600"><strong>Nota:</strong> Por razones de seguridad, solo personal autorizado puede realizar cambios en las credenciales.</p>
                </div>
              </div>
              <button
                onClick={handleBackToLogin}
                className="w-full py-3 px-4 bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white rounded-lg font-medium transition-all"
              >
                ← Volver al Inicio de Sesión
              </button>
            </div>
          ) : (
            <div>
              {/* Login Form */}
              <div className="space-y-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    {loginType === 'master' ? 'Identificador Maestro' : 'Correo Electrónico'}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      {loginType === 'master' ? <Shield className="h-5 w-5 text-red-400" /> : <Mail className="h-5 w-5 text-orange-400" />}
                    </div>
                    <input
                      type={loginType === 'master' ? 'text' : 'email'}
                      id="email" name="email" value={formData.email} onChange={handleInputChange}
                      className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
                        errors.email ? 'border-red-300 focus:ring-red-500' :
                        loginType === 'master' ? 'border-red-300 focus:ring-red-500' :
                        'border-gray-300 focus:ring-orange-500 hover:border-orange-300'
                      }`}
                      placeholder={loginType === 'master' ? 'Identificador del sistema maestro' : 'admin@jaydevspr.com'}
                    />
                  </div>
                  {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">Contraseña</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className={`h-5 w-5 ${loginType === 'master' ? 'text-red-400' : 'text-orange-400'}`} />
                    </div>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password" name="password" value={formData.password} onChange={handleInputChange}
                      className={`w-full pl-10 pr-12 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
                        errors.password ? 'border-red-300 focus:ring-red-500' :
                        loginType === 'master' ? 'border-red-300 focus:ring-red-500' :
                        'border-gray-300 focus:ring-orange-500 hover:border-orange-300'
                      }`}
                      placeholder="••••••••"
                    />
                    <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center" onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <EyeOff className={`h-5 w-5 ${loginType === 'master' ? 'text-red-400' : 'text-orange-400'}`} /> : <Eye className={`h-5 w-5 ${loginType === 'master' ? 'text-red-400' : 'text-orange-400'}`} />}
                    </button>
                  </div>
                  {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password}</p>}
                </div>

                {loginType === 'admin' && (
                  <div className="text-right">
                    <button type="button" className="text-sm text-orange-600 hover:text-orange-800 transition-colors font-medium" onClick={handleForgotPassword}>
                      ¿Olvidaste tu contraseña?
                    </button>
                  </div>
                )}

                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className={`w-full py-3 px-4 rounded-lg font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 transition-all ${
                    loginType === 'master' ? 'bg-gradient-to-r from-red-600 to-red-800 hover:from-red-700 hover:to-red-900 focus:ring-red-500' :
                    'bg-gradient-to-r from-black to-gray-800 hover:from-gray-800 hover:to-black focus:ring-orange-500'
                  }`}
                >
                  {loading ? (
                    <div className="flex items-center justify-center"><div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div><span className="ml-2">Iniciando sesión...</span></div>
                  ) : (
                    <div className="flex items-center justify-center">{loginType === 'master' ? <Shield className="h-5 w-5 mr-2" /> : <User className="h-5 w-5 mr-2" />} {loginType === 'master' ? 'Acceso Maestro' : 'Iniciar Sesión'}</div>
                  )}
                </button>

                {/* Enhanced Error Display */}
                {errors.submit && (
                  <div className={`mt-4 p-4 border rounded-lg flex items-start space-x-3 ${
                    errors.status ? 'bg-yellow-50 border-yellow-300' : 'bg-red-50 border-red-200'
                  }`}>
                    <AlertCircle className={`h-5 w-5 mt-0.5 flex-shrink-0 ${
                      errors.status ? 'text-yellow-500' : 'text-red-500'
                    }`} />
                    <div>
                      <p className={`text-sm font-medium ${
                        errors.status ? 'text-yellow-800' : 'text-red-800'
                      }`}>
                        {errors.submit}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-500 mb-2">Protegido por seguridad empresarial</p>
            <p className="text-xs text-orange-600 font-medium">© 2024 JaydevsPR - Desarrollando conexiones</p>
          </div>
        </div>
      </div>
    </div>
  );
}